/**
 * tracker.js — Engine di tracking: dataLayer push + source tagging.
 *
 * Tutte le funzioni sono no-op se tracking è disabilitato in config.
 * Esposto globalmente come window.ikTracking per accesso dal consent store.
 */

const SOURCE_KEY = 'source_frontend';

function getSource() {
    return window.IKOS?.tracking?.source || 'frontend-v2';
}

function isEnabled() {
    return window.IKOS?.tracking?.enabled !== false;
}

function ensureDataLayer() {
    window.dataLayer = window.dataLayer || [];
}

// ── Push generico ───────────────────────────────────────

/**
 * Push generico al dataLayer.
 */
export function track(eventName, data = {}) {
    if (!isEnabled()) return;
    ensureDataLayer();

    window.dataLayer.push({
        event: eventName,
        [SOURCE_KEY]: getSource(),
        ...data,
    });
    logToLedger(eventName, data, null);
}

// ── E-commerce (GA4 enhanced ecommerce) ────────────────

/**
 * Push evento e-commerce. Fa il clear dell'oggetto ecommerce prima del push
 * come richiesto da GA4 per evitare contaminazione tra eventi.
 *
 * @param {object} extra  parametri non e-commerce da affiancare all'evento
 *                        (es. cta_location su add_to_cart): finiscono al primo
 *                        livello del push, non dentro `ecommerce`.
 */
export function trackEcommerce(eventName, ecommerceData, extra = {}) {
    if (!isEnabled()) return;
    ensureDataLayer();

    // Clear previous ecommerce object
    // content_category: una sola categoria degli item o "Misto". Lo legge il tag
    // Meta in GTM e deve coincidere con l'evento server deduplicato (stesso event_id).
    if (ecommerceData && !ecommerceData.content_category) {
        const category = contentCategory(ecommerceData.items);
        if (category) ecommerceData = { ...ecommerceData, content_category: category };
    }
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: eventName,
        [SOURCE_KEY]: getSource(),
        ...extra,
        ecommerce: ecommerceData,
    });
    logToLedger(eventName, {}, ecommerceData);
}

// ── Registro locale degli eventi browser ───────────────

/**
 * Eventi del funnel che finiscono nel registro locale (tbl_trackingevents,
 * destination=browser) tramite ajax/track-event.php in modalità log: nessun
 * invio esterno, solo la copia in casa per il funnel per corso. Login e
 * rimozioni restano fuori: non dicono nulla sul funnel.
 */
const LEDGER_EVENTS = new Set([
    'view_item_list', 'select_item', 'view_item', 'add_to_cart', 'view_cart',
    'begin_checkout', 'add_payment_info', 'order_created', 'payment_initiated',
    'payment_completed', 'purchase', 'free_enrollment', 'sign_up', 'add_to_wishlist',
    // Blocco "Dicci chi sei" nel catalogo: quanti scelgono la professione, quanti lo chiudono
    'select_profession', 'close_profession_prompt', 'decline_profession_prompt', 'ignore_profession_prompt', 'forget_profession',
]);

function logToLedger(eventName, data, ecommerce) {
    if (!LEDGER_EVENTS.has(eventName)) return;
    const base = window.IKOS?.ajaxBase;
    if (!base) return;

    const items = (ecommerce && ecommerce.items) || data.items || [];
    const eventId = (ecommerce && ecommerce.event_id) || data.event_id || generateEventId(eventName);
    const payload = {
        log: true,
        event: eventName,
        event_id: eventId,
        source_url: window.location.href,
        value: ecommerce ? ecommerce.value : data.value,
        content_ids: items.map(i => String(i.item_id || '')).filter(Boolean).slice(0, 50),
        content_category: (ecommerce && ecommerce.content_category) || data.content_category || contentCategory(items),
        list_id: ecommerce ? ecommerce.item_list_id : undefined,
        orders_id: (ecommerce && ecommerce.transaction_id) || data.orders_id,
        circuit: data.circuit,
        method: data.method,
        payment_type: ecommerce ? ecommerce.payment_type : data.payment_type,
        courses_id: data.courses_id,
        // Box professione: cosa è stato scelto e come (elenco o conferma "Sei un X?")
        occupation: data.occupation,
        occupation_id: data.occupation_id,
        discipline: data.discipline,
        variant: data.variant,
        how: data.how,
    };
    sendToRelay(base, payload);
}

function sendToRelay(base, payload) {
    const body = JSON.stringify(payload);
    const url = `${base}/track-event.php`;
    try {
        if (navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
            return;
        }
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    } catch (e) { /* tracking silenzioso */ }
}

// ── Consent Mode v2 ────────────────────────────────────

/**
 * Aggiorna il consenso GTM Consent Mode v2.
 * Chiamato dal consent store dopo che l'utente fa una scelta.
 */
export function updateConsent(categories) {
    ensureDataLayer();
    function gtag() { window.dataLayer.push(arguments); }
    gtag('consent', 'update', {
        'analytics_storage':  categories.analytics ? 'granted' : 'denied',
        'ad_storage':         categories.marketing ? 'granted' : 'denied',
        'ad_user_data':       categories.marketing ? 'granted' : 'denied',
        'ad_personalization': categories.marketing ? 'granted' : 'denied',
    });
}

// ── Helpers ─────────────────────────────────────────────

/**
 * Costruisce un GA4 item da un oggetto corso/cart item.
 * Accetta sia la risposta API (item con title, type, price)
 * sia il body della request (con courses_id).
 */
export function buildGA4Item(item, requestBody) {
    return {
        item_id:       String(item?.id || item?.courses_id || requestBody?.courses_id || ''),
        item_name:     item?.title || item?.name || '',
        item_category: item?.category || item?.course_type || item?.type || item?.product_type || 'course',
        item_variant:  item?.code || '',
        price:         parseFloat(item?.price || item?.credit_price || 0),
        quantity:      1,
    };
}

/**
 * Categoria di tracking di una lista di item: FAD, Residenziale, Global Card,
 * Pacchetto (vocabolario di api/src/category.php), "Misto" se piu' di una.
 */
export function contentCategory(items) {
    const known = ['FAD', 'Residenziale', 'Global Card', 'Pacchetto'];
    const set = new Set((items || []).map(i => i && i.item_category).filter(c => known.includes(c)));
    if (set.size === 0) return undefined;
    return set.size === 1 ? [...set][0] : 'Misto';
}

/**
 * Numero in formato italiano ("1.046,00") o già numerico → float.
 */
export function parseAmount(value) {
    if (typeof value === 'number') return value;
    return parseFloat(String(value || '0').replace(/\./g, '').replace(',', '.')) || 0;
}

/**
 * Costruisce gli item GA4 dalle righe di un ordine (/v1/users/me/orders/{id}).
 * item_id è l'id numerico del corso (courses_id, presente nella risposta del
 * tracking-claim), con fallback sul codice: deve coincidere con gli eventi carrello.
 */
export function buildOrderItems(order) {
    const rows = order?.rows || order?.items || [];
    return rows.map(r => ({
        item_id:       String(r.courses_id || r.code || r.id || ''),
        item_name:     r.description || r.title || r.name || '',
        item_category: r.course_type || r.category || r.type || 'course',
        item_variant:  r.code || '',
        price:         r.total_gross !== undefined ? parseAmount(r.total_gross) : parseAmount(r.total),
        quantity:      1,
    }));
}

/**
 * event_id condiviso tra browser e server per la deduplica Meta Pixel ↔ CAPI:
 * derivato dall'ordine, così i due lati coincidono senza doversi passare nulla.
 */
export function purchaseEventId(orderId) {
    return `purchase_${orderId}`;
}

// ── Relay server-side (Meta CAPI) ──────────────────────

/**
 * Manda al server la copia di un evento di funnel (AddToCart, InitiateCheckout,
 * ViewContent, ...), con lo stesso event_id pushato nel dataLayer: il server la
 * inoltra alla Conversions API (ajax/track-event.php). sendBeacon sopravvive
 * alla navigazione e non blocca l'interfaccia.
 */
export function relay(metaEvent, payload = {}) {
    if (!isEnabled()) return;
    const base = window.IKOS?.ajaxBase;
    if (!base) return;

    sendToRelay(base, {
        event: metaEvent,
        source_url: window.location.href,
        ...payload,
    });
}

/**
 * Genera un event_id univoco per deduplicazione Meta CAPI.
 * Formato: {prefix}_{timestamp}_{random}
 */
export function generateEventId(prefix = 'evt') {
    const ts = Date.now();
    const rand = Math.random().toString(36).substring(2, 8);
    return `${prefix}_${ts}_${rand}`;
}
